/**
 * Curva de caja (13 semanas / 12 meses) — STUB DE FASE 3.
 *
 * Estado actual: pinta el esqueleto del grafico con Recharts si llegan puntos,
 * y en su defecto un marcador honesto. Lo que NO hace todavia:
 *  - Banda de incertidumbre (banda_min/banda_max).
 *  - Comparativa entre escenario base y escenario hipotetico.
 *  - Interaccion tactil de "¿y si…?" sobre el propio grafico.
 *
 * Criterio de diseno kiosco: pocos puntos de eje, tipografia grande, la linea
 * roja del umbral de seguridad siempre visible y el dia de caja minima
 * anotado EN PALABRAS debajo del grafico —el jefe no debe leer un tooltip.
 *
 * TODO(Fase3): implementar bandas, escenarios y el resto de lo anterior.
 */
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Tarjeta, TarjetaCabecera, TarjetaTitulo, TarjetaContenido } from '@/components/ui/card';
import { formatearEuros, formatearFechaLarga, cn } from '@/lib/utils';
import type { CurvaPrevision } from '@/lib/api';

interface PropsPrevisionChart {
  curva: CurvaPrevision | null;
  cargando?: boolean;
  className?: string;
}

export function PrevisionChart({ curva, cargando = false, className }: PropsPrevisionChart) {
  return (
    <Tarjeta className={cn('min-h-[28rem]', className)}>
      <TarjetaCabecera>
        <TarjetaTitulo>Como va la caja</TarjetaTitulo>
        <p className="text-sm text-muted-foreground">
          {curva
            ? `Proximos ${curva.horizonte_dias} dias${
                curva.escenario_nombre ? ` · escenario: ${curva.escenario_nombre}` : ''
              }`
            : 'Prevision de tesoreria'}
        </p>
      </TarjetaCabecera>

      <TarjetaContenido className="flex flex-col gap-6">
        {cargando && <p className="text-lg text-muted-foreground">Calculando la prevision…</p>}

        {!cargando && (!curva || curva.puntos.length === 0) && <MarcadorStub />}

        {!cargando && curva && curva.puntos.length > 0 && (
          <>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={curva.puntos}
                  margin={{ top: 12, right: 24, bottom: 8, left: 12 }}
                >
                  <CartesianGrid strokeDasharray="4 6" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="fecha"
                    tick={{ fontSize: 18, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(v: string) => formatearFechaLarga(v).replace(' de ', ' ')}
                    minTickGap={64}
                    stroke="hsl(var(--border))"
                  />
                  <YAxis
                    tick={{ fontSize: 18, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
                    width={72}
                    stroke="hsl(var(--border))"
                  />
                  <Tooltip
                    formatter={(v) => formatearEuros(Number(v))}
                    labelFormatter={(v) => formatearFechaLarga(String(v))}
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '2px solid hsl(var(--border))',
                      borderRadius: '0.75rem',
                      fontSize: '1.25rem',
                      color: 'hsl(var(--popover-foreground))',
                    }}
                  />

                  {/* Umbral de seguridad: la linea que no se debe cruzar. */}
                  {curva.umbral_seguridad != null && (
                    <ReferenceLine
                      y={curva.umbral_seguridad}
                      stroke="hsl(var(--pago))"
                      strokeWidth={3}
                      strokeDasharray="8 6"
                    />
                  )}

                  {/* Real hasta hoy, previsto a partir de ahi. */}
                  <Area
                    type="monotone"
                    dataKey="saldo_real"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary))"
                    fillOpacity={0.18}
                    strokeWidth={4}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="saldo_previsto"
                    stroke="hsl(var(--accent))"
                    strokeWidth={4}
                    strokeDasharray="10 6"
                    dot={false}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* La conclusion, en palabras. Esto es lo que de verdad lee el jefe. */}
            {curva.minimo_fecha && curva.minimo_importe != null && (
              <p className="text-lg">
                El dia mas justo es el{' '}
                <strong>{formatearFechaLarga(curva.minimo_fecha)}</strong>, con{' '}
                <strong className="cifra">{formatearEuros(curva.minimo_importe)}</strong>.
              </p>
            )}
          </>
        )}
      </TarjetaContenido>
    </Tarjeta>
  );
}

/** Marcador explicito de stub: mejor decir la verdad que fingir un grafico. */
function MarcadorStub() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-border p-10 text-center">
      <p className="text-lg font-semibold">Aun no tengo prevision que ensenarle.</p>
      <p className="text-base text-muted-foreground">
        La curva de caja llega en la Fase 3, cuando esten los conectores de facturacion y nominas.
      </p>
    </div>
  );
}
